import { Link } from 'react-router-dom';
import { IconBook } from '@tabler/icons-react';
import { useShellProfile } from '../../state/useShellProfile';
import { useListPortalResourcesQuery } from '../../state/domainRouterApi';
import { SupportErrorMessage } from '../../components/overlays/EngineeringSupportBanner';

const Empty = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-irongrey/50 font-medium">{children}</p>
);

const ErrorState = ({ children }: { children: React.ReactNode }) => (
  <SupportErrorMessage>{children}</SupportErrorMessage>
);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const readText = (value: unknown): string => String(value ?? '').trim();

const withQuery = (pathname: string, query: Record<string, string>): string => {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    const normalized = String(value ?? '').trim();
    if (!normalized) return;
    search.set(key, normalized);
  });
  const encoded = search.toString();
  return encoded ? `${pathname}?${encoded}` : pathname;
};

type ResourceRowProps = {
  record: Record<string, unknown>;
  rowKey: string;
};

const ResourceRow = ({ record, rowKey }: ResourceRowProps) => {
  const resourceId = readText(record.id) || rowKey;
  const name = readText(record.name ?? record.title ?? `Resource ${rowKey}`);
  const meta = readText(record.meta ?? record.description);
  const url = readText(record.url ?? record.downloadUrl);
  const internalTarget = withQuery('/parent/portal/resources', { resourceId });
  const target = url || internalTarget;
  const isExternal = /^https?:\/\//i.test(target);

  return (
    <li className="border-t border-gray-100 first:border-0">
      <article className="px-6 py-4 flex items-center justify-between gap-4">
        <section className="min-w-0">
          <h2 className="text-sm font-bold text-irongrey truncate">{name}</h2>
          {meta ? <p className="text-xs text-irongrey/60 font-medium mt-1 truncate">{meta}</p> : null}
        </section>

        {isExternal ? (
          <a
            href={target}
            target="_blank"
            rel="noreferrer"
            className="px-4 py-2 rounded-full border border-gray-200 text-irongrey font-bold text-xs hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            View
          </a>
        ) : (
          <Link
            to={target.startsWith('/') ? target : internalTarget}
            className="px-4 py-2 rounded-full border border-gray-200 text-irongrey font-bold text-xs hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            View
          </Link>
        )}
      </article>
    </li>
  );
};

export function PortalResourcesPage() {
  const { profile } = useShellProfile();
  const actorId = profile?.userSub ?? null;
  const resourcesQuery = useListPortalResourcesQuery(undefined, { skip: !actorId });
  const resources = resourcesQuery.data ?? [];

  return (
    <main
      data-testid="parent-portal-resources"
      data-extracted-from="owned:ystnj/parent-portal/resources"
      className="w-full space-y-6"
    >
      <header className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center" aria-hidden>
          <IconBook size={20} className="text-primary" />
        </span>
        <section>
          <h1 className="text-2xl font-bold text-irongrey tracking-tight">Resources</h1>
          <p className="text-sm text-irongrey/60 font-medium mt-1">Handbooks, calendars, and downloads.</p>
          <p className="sr-only">
            Resource center listing policy handbooks, academic calendars, required forms, and parent download links.
          </p>
        </section>
      </header>

      {!actorId ? <ErrorState>Unable to resolve your account identity.</ErrorState> : null}
      {resourcesQuery.isLoading ? <Empty>Loading resources...</Empty> : null}
      {resourcesQuery.error ? <ErrorState>Unable to load resources right now.</ErrorState> : null}

      <section className="bg-white border border-gray-100 rounded-3xl shadow-card overflow-hidden" aria-label="Parent resources">
        {!resourcesQuery.isLoading && !resourcesQuery.error && resources.length === 0 ? (
          <section className="px-6 py-6">
            <Empty>No resources have been published yet.</Empty>
          </section>
        ) : null}

        <ul>
          {resources.map((resource, idx) => {
            const record = asRecord(resource);
            const key = `${readText(record.id) || 'resource'}-${idx}`;
            return <ResourceRow key={key} record={record} rowKey={key} />;
          })}
        </ul>
      </section>
    </main>
  );
}
